import React, { useState } from "react";
import {
    Box,
    Stack,
    TextField,
    Typography,
    Button,
    Alert,
    useTheme,
} from "@mui/material";
import { fetchWithAuth } from "../../utils/api";
import { useItemValidator } from "../../hooks/useItemValidator";

const AdminPasteTrades = () => {
    const theme = useTheme();
    const { validate } = useItemValidator();

    const [username, setUsername] = useState("");
    const [rawText, setRawText] = useState("");
    const [parsedTrades, setParsedTrades] = useState([]);
    const [errors, setErrors] = useState([]);
    const [validated, setValidated] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");

    const getCaliforniaToday = () => {
        const now = new Date();
        const pst = new Date(
            now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
        );
        pst.setHours(0, 0, 0, 0);
        return pst;
    };

    const isValidTradeDate = (dateStr) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;

        const [y, m, d] = dateStr.split("-").map(Number);
        const date = new Date(y, m - 1, d);
        date.setHours(0, 0, 0, 0);

        return (
            date.getFullYear() === y &&
            date.getMonth() === m - 1 &&
            date.getDate() === d &&
            date <= getCaliforniaToday()
        );
    };

    const SENT_REGEX = /(items?\s+sent|sent)\s*[:\uFF1A]/i;
    const RECEIVED_REGEX = /(items?\s+received|received)\s*[:\uFF1A]/i;
    const NOTES_REGEX = /notes\s*[:\uFF1A]/i;

    const cleanLine = (line) =>
        line
            .replace(/\uFEFF/g, "")      // BOM
            .replace(/\u200B/g, "")      // zero-width space
            .replace(/\u00A0/g, " ")     // non-breaking space
            .trim();

    const parsePastedTrades = (text) => {
        if (!text || !text.trim()) {
            throw new Error("Paste input is empty.");
        }

        // Normalize line endings
        const normalized = text
            .replace(/\r\n/g, "\n")
            .replace(/\r/g, "\n");

        const blocks = normalized.trim().split(/\n\s*\n/);
        const trades = [];

        blocks.forEach((block, index) => {
            const rawLines = block.split("\n");
            const lines = rawLines.map(cleanLine);

            if (lines.length < 4) {
                throw new Error(`Trade ${index + 1}: Expected 4 lines`);
            }

            const dateLine = lines[0];
            const sentLine = lines[1];
            const receivedLine = lines[2];
            const notesLine = lines[3];

            if (!isValidTradeDate(dateLine)) {
                throw new Error(`Trade ${index + 1}: Invalid or future trade date`);
            }

            if (!SENT_REGEX.test(sentLine)) {
                throw new Error(`Trade ${index + 1}: Missing or invalid Sent line`);
            }

            if (!RECEIVED_REGEX.test(receivedLine)) {
                throw new Error(`Trade ${index + 1}: Missing or invalid Received line`);
            }

            if (!NOTES_REGEX.test(notesLine)) {
                throw new Error(`Trade ${index + 1}: Missing Notes line`);
            }

            trades.push({
                tradeDate: dateLine,
                itemsSent: sentLine.replace(SENT_REGEX, "").trim(),
                itemsReceived: receivedLine.replace(RECEIVED_REGEX, "").trim(),
                notes: notesLine.replace(NOTES_REGEX, "").trim(),
            });
        });

        return trades;
    };

    const handleValidate = async () => {
        setErrors([]);
        setValidated(false);

        try {
            if (!username.trim()) {
                throw new Error("Username is required.");
            }
            if (!rawText.trim()) {
                throw new Error("Paste content is empty.");
            }

            const trades = parsePastedTrades(rawText);

            // Client-side item validation
            trades.forEach((t, i) => {
                const sent = validate(t.itemsSent);
                const received = validate(t.itemsReceived);

                if (sent.errors.length) {
                    throw new Error(`Trade ${i + 1}: ${sent.errors.join("; ")}`);
                }
                if (received.errors.length) {
                    throw new Error(`Trade ${i + 1}: ${received.errors.join("; ")}`);
                }
            });

            // Server-side validation (authoritative)
            await fetchWithAuth("/report/paste?validateOnly=true", {
                method: "POST",
                body: {
                    username,
                    trades,
                },
            });

            setParsedTrades(trades);
            setValidated(true);
        } catch (err) {
            setErrors([err.message || "Validation failed."]);
        }
    };

    const handleSubmit = async () => {
        try {
            await fetchWithAuth("/report/paste", {
                method: "POST",
                body: {
                    username,
                    trades: parsedTrades,
                },
            });

            setSubmitted(true);
            setSuccessMsg(
                `Successfully submitted ${parsedTrades.length} trades as user ${username}`
            );
        } catch (err) {
            setErrors([err.message || "Submission failed."]);
        }
    };

    return (
        <Box sx={{ maxWidth: 900, mx: "auto", mt: 5, p: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, color: theme.palette.accent.main }}>
                Admin: Paste Trades (Single User)
            </Typography>

            <Stack spacing={3}>
                {errors.length > 0 && (
                    <Alert severity="error">{errors.join("\n")}</Alert>
                )}

                {validated && !submitted && (
                    <Alert severity="success">
                        {parsedTrades.length} trades validated successfully.
                    </Alert>
                )}

                {submitted && (
                    <Alert severity="success">{successMsg}</Alert>
                )}

                <TextField
                    label="Username"
                    value={username}
                    onChange={(e) => {
                        setUsername(e.target.value);
                        setValidated(false);
                        setParsedTrades([]);
                        setSuccessMsg("");
                    }}
                    disabled={submitted}
                    fullWidth
                />

                <TextField
                    label="Paste trade reports"
                    value={rawText}
                    onChange={(e) => {
                        setRawText(e.target.value);
                        setValidated(false);
                        setParsedTrades([]);
                        setSuccessMsg("");
                    }}
                    disabled={submitted}
                    fullWidth
                    multiline
                    minRows={10}
                />

                <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        onClick={handleValidate}
                        disabled={submitted}
                    >
                        Validate
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!validated || submitted}
                    >
                        Submit
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};

export default AdminPasteTrades;
